import { useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  TextField,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import ResponsiveDialog from "../UI/ResponsiveDialog";
import { useMutation } from "@apollo/client";
import {
  ADD_TRANSFER_REQUEST,
  GET_TRANSFER_REQUESTS_BY_PLAYER_ACCEPTED_OR_TEAM_ACCEPTED,
  GET_TRANSFER_REQUESTS_BY_PLAYER_ACCEPTED_AND_TEAM_ACCEPTED,
  TRANSFER_REQUESTS_WITH_PERMISSION,
} from "../../GraphQL/TransferRequests.js";
import useNotification from "../../hooks/useNotification.js";
import PlayerSelect from "../Player/PlayerSelect";
import TeamSelect from "../Team/TeamSelect";

const NewTransferRequestDialog = (props) => {
  const [transferRequest, setTransferRequest] = useState({
    playerAccepted: false,
    teamAccepted: false,
  });
  const [player, setPlayer] = useState("");
  const [team, setTeam] = useState("");
  const { notifySuccess } = useNotification();

  const isValid =
    player !== "" && team !== "" && !!(transferRequest.requestDate?.trim());

  const [addTransferRequest, { loading, error }] = useMutation(
    ADD_TRANSFER_REQUEST,
    {
      variables: {
        playerId: player,
        teamId: team,
        playerAccepted: transferRequest.playerAccepted,
        teamAccepted: true,
        requestDate: transferRequest.requestDate,
      },
      refetchQueries: [
        GET_TRANSFER_REQUESTS_BY_PLAYER_ACCEPTED_OR_TEAM_ACCEPTED,
        GET_TRANSFER_REQUESTS_BY_PLAYER_ACCEPTED_AND_TEAM_ACCEPTED,
        TRANSFER_REQUESTS_WITH_PERMISSION,
      ],
      onCompleted: () => { notifySuccess("Transfer request created."); props.setOpen(false); },
    }
  );

  return (
    <div>
      <ResponsiveDialog
        open={props.open}
        onKeyDown={(e) => {
          if (e.key === "Enter" && isValid) {
            addTransferRequest();
          } else if (e.key === "Escape") {
            props.setOpen(false);
          }
        }}
        title="Transfer Request"
        onClose={() => props.setOpen(false)}
      >
        <DialogContent>
          <PlayerSelect
            player={player}
            setPlayer={setPlayer}
            players={props.players}
          />
          <TeamSelect teams={props.teams} team={team} setTeam={setTeam} />
          <FormControlLabel
            label="Player Accepted"
            control={
              <Checkbox
                data-testid="playerAccepted"
                autoFocus
                margin="dense"
                id="playerAccepted"
                label="Player Accepted"
                variant="standard"
                disabled
                onChange={(e) => {
                  setTransferRequest({
                    ...transferRequest,
                    playerAccepted: e.target.checked,
                  });
                }}
              />
            }
          />
          <FormControlLabel
            label="Team Accepted"
            control={
              <Checkbox
                data-testid="teamAccepted"
                margin="dense"
                id="teamAccepted"
                label="Team Accepted"
                variant="standard"
                checked
                disabled
              />
            }
          />
          <TextField
            data-testid="requestDate"
            margin="dense"
            id="requestDate"
            label="Request Date"
            type="date"
            fullWidth
            variant="standard"
            onChange={(e) => {
              setTransferRequest({
                ...transferRequest,
                requestDate: e.target.value,
              });
            }}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        {error && (
          <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
            {error.message}
          </Alert>
        )}
        <DialogActions>
          <Button
            data-testid="saveNewTransferRequestDialogButton"
            variant="contained"
            onClick={() => addTransferRequest()}
            disabled={loading || !isValid}
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: "inherit" }} />
            ) : (
              "Create"
            )}
          </Button>
          <Button
            data-testid="closeNewTransferRequestDialogButton"
            onClick={() => props.setOpen(false)}
          >
            Cancel
          </Button>
        </DialogActions>
      </ResponsiveDialog>
    </div>
  );
};

export default NewTransferRequestDialog;
